import { useState, useEffect } from "react";
import { sendOtp, connectWooCommerceWithOtp } from "../../utils/api/shopApi";
import OtpInput from "./OtpInput";
import ApiErrorToast from "./ApiErrorToast";
import styles from "./ConnectModal.module.css";

const WooCommerceConnectModal = ({ isOpen, onClose, onConnect }) => {
  const [formData, setFormData] = useState({
    storeUrl: "",
    consumerKey: "",
    consumerSecret: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState("credentials"); // credentials, email, otp, success
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [storeName, setStoreName] = useState("");
  const [countdown, setCountdown] = useState(5);
  const [apiError, setApiError] = useState(null);

  const validateUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === "http:" || urlObj.protocol === "https:";
    } catch {
      return false;
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.storeUrl.trim()) {
      newErrors.storeUrl = "Store URL is required";
    } else if (!validateUrl(formData.storeUrl)) {
      newErrors.storeUrl = "Please enter a valid HTTP or HTTPS URL";
    }

    if (!formData.consumerKey.trim()) {
      newErrors.consumerKey = "Consumer Key is required";
    }

    if (!formData.consumerSecret.trim()) {
      newErrors.consumerSecret = "Consumer Secret is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await onConnect(formData);
      if (response.ok && response.data?.storeName) {
        setStoreName(response.data.storeName);
        setCurrentStep("email");
        setErrors({});
      }
    } catch (error) {
      console.error("WooCommerce validation failed:", error);
      setApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendOtp = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrors({ email: "Please enter a valid email address" });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      await sendOtp(email);
      setCurrentStep("otp");
    } catch (error) {
      console.error("Failed to send OTP:", error);
      setApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpComplete = (otpValue) => {
    setOtp(otpValue);
  };

  const handleConnectWithOtp = async () => {
    if (!otp || otp.length !== 6) {
      setErrors({ otp: "Please enter a valid 6-digit OTP" });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await connectWooCommerceWithOtp({
        email,
        otp,
        storeUrl: formData.storeUrl,
        consumerKey: formData.consumerKey,
        consumerSecret: formData.consumerSecret,
        requireWrite: false,
      });

      if (response.ok && response.data?.storeName) {
        setCurrentStep("success");
        setStoreName(response.data.storeName);

        // Start countdown - use setTimeout to defer onClose call to avoid
        // updating parent component during render
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              // Defer onClose to avoid calling setState in parent during render
              setTimeout(() => onClose(), 0);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      console.error("WooCommerce connection with OTP failed:", error);
      setApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  // Reset modal state when closing
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep("credentials");
      setEmail("");
      setOtp("");
      setStoreName("");
      setCountdown(5);
      setFormData({ storeUrl: "", consumerKey: "", consumerSecret: "" });
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className={styles.modalHeader}>
          <img
            src="/assets/woocommerce.png"
            alt="WooCommerce"
            width={40}
            height={40}
          />
          <h2>
            {currentStep === "credentials" && "Connect WooCommerce Store"}
            {currentStep === "email" && "Validated successfully"}
            {currentStep === "otp" && "Enter OTP"}
            {currentStep === "success" && "Connection Successful!"}
          </h2>
          <p>
            {currentStep === "credentials" &&
              "Enter your WooCommerce API credentials to connect your store"}
            {currentStep === "email" &&
              "Please provide your email to receive an OTP for verification"}
            {currentStep === "otp" &&
              "Enter the 6-digit OTP sent to your email"}
            {currentStep === "success" &&
              `${storeName} is successfully connected!`}
          </p>
        </div>

        {currentStep === "credentials" && (
          <form onSubmit={handleCredentialsSubmit} className={styles.form}>
            <div className={styles.inputGroup}>
              <label htmlFor="storeUrl">Store URL</label>
              <input
                id="storeUrl"
                type="url"
                value={formData.storeUrl}
                onChange={(e) => handleChange("storeUrl", e.target.value)}
                placeholder="https://yourwoostore.com"
                className={errors.storeUrl ? styles.inputError : ""}
                disabled={isSubmitting}
              />
              {errors.storeUrl && (
                <span className={styles.errorText}>{errors.storeUrl}</span>
              )}
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="consumerKey">Consumer Key</label>
              <input
                id="consumerKey"
                type="text"
                value={formData.consumerKey}
                onChange={(e) => handleChange("consumerKey", e.target.value)}
                placeholder="ck_***"
                className={errors.consumerKey ? styles.inputError : ""}
                disabled={isSubmitting}
              />
              {errors.consumerKey && (
                <span className={styles.errorText}>{errors.consumerKey}</span>
              )}
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="consumerSecret">Consumer Secret</label>
              <input
                id="consumerSecret"
                type="password"
                value={formData.consumerSecret}
                onChange={(e) => handleChange("consumerSecret", e.target.value)}
                placeholder="cs_***"
                className={errors.consumerSecret ? styles.inputError : ""}
                disabled={isSubmitting}
              />
              {errors.consumerSecret && (
                <span className={styles.errorText}>
                  {errors.consumerSecret}
                </span>
              )}
            </div>

            {errors.submit && (
              <span className={styles.errorText}>{errors.submit}</span>
            )}

            <div className={styles.buttonGroup}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Validating..." : "Connect Store"}
              </button>
            </div>
          </form>
        )}

        {currentStep === "email" && (
          <div className={styles.form}>
            <div className={styles.inputGroup}>
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder="your@email.com"
                className={errors.email ? styles.inputError : ""}
                disabled={isSubmitting}
              />
              {errors.email && (
                <span className={styles.errorText}>{errors.email}</span>
              )}
            </div>

            <div className={styles.buttonGroup}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => setCurrentStep("credentials")}
                disabled={isSubmitting}
              >
                Back
              </button>
              <button
                type="button"
                className={styles.submitButton}
                onClick={handleSendOtp}
                disabled={
                  isSubmitting ||
                  !email.trim() ||
                  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
                }
              >
                {isSubmitting ? "Sending..." : "Send OTP"}
              </button>
            </div>
          </div>
        )}

        {currentStep === "otp" && (
          <div className={styles.form}>
            <div className={styles.inputGroup}>
              <label>Enter 6-digit OTP</label>
              <OtpInput
                length={6}
                onComplete={handleOtpComplete}
                disabled={isSubmitting}
              />
              {errors.otp && (
                <span className={styles.errorText}>{errors.otp}</span>
              )}
            </div>

            <div className={styles.buttonGroup}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => setCurrentStep("email")}
                disabled={isSubmitting}
              >
                Back
              </button>
              <button
                type="button"
                className={styles.submitButton}
                onClick={handleConnectWithOtp}
                disabled={isSubmitting || !otp || otp.length !== 6}
              >
                {isSubmitting ? "Connecting..." : "Connect"}
              </button>
            </div>
          </div>
        )}

        {currentStep === "success" && (
          <div className={styles.successContent}>
            <div className={styles.successIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="#10b981" opacity="0.15" />
                <path
                  d="M8 12.5l3.5 3.5L16 8"
                  stroke="#10b981"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3 className={styles.successTitle}>
              {storeName} is successfully connected!
            </h3>
            <div className={styles.countdownContainer}>
              <span className={styles.countdownText}>Closing in</span>
              <span className={styles.countdownNumber}>{countdown}</span>
              <span className={styles.countdownText}>seconds</span>
            </div>
          </div>
        )}
        <ApiErrorToast error={apiError} onClose={() => setApiError(null)} />
      </div>
    </div>
  );
};

export default WooCommerceConnectModal;
